import {Breadcrumb, Button, Select, Table, Tag} from "antd";
import {ColumnsType} from "antd/es/table";
import React, {useContext, useEffect, useMemo, useState} from "react";
import {ReloadOutlined} from "@ant-design/icons";
import {AppContextContext, QueueServiceContext} from "../../Contexts";
import QueueMessageSummaryRow from "../../domain/QueueMessageSummaryRow";
import QueueMessage from "../../domain/QueueMessage";
import PagedResult from "../../service/PagedResult";
import {QueueMessageFilter, QueueMessageStatus} from "../../service/QueueService";
import {useTableHandler} from "../../sal-ui/TableHandler";
import {descendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import {DocumentTitle} from "../DocumentTitle";
import {formatDateTime} from "../../utils/FormatUtils";
import * as globalStyles from "../App.module.css";

function QueuesPage() {
    const appContext = useContext(AppContextContext);
    const queueService = useContext(QueueServiceContext);
    const tableHandler = useTableHandler("queuedAt asc", {reloadFunction: reloadMessages});
    const [summary, setSummary] = useState<QueueMessageSummaryRow[]>([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [messages, setMessages] = useState<PagedResult<QueueMessage>>();
    const [filter, setFilter] = useState<QueueMessageFilter>({});
    const title = "Queues";

    const typeOptions = useMemo(
        () => summary.map(row => ({value: row.type, label: row.type})),
        [summary]
    );

    const statusOptions: { value: QueueMessageStatus; label: string }[] = [
        {value: "ready", label: "Ready"},
        {value: "scheduled", label: "Scheduled"},
    ];

    const errorOptions: { value: boolean; label: string }[] = [
        {value: true, label: "With last error"},
        {value: false, label: "Without last error"},
    ];

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        reloadSummary();
    }, []);

    useEffect(() => {
        tableHandler.resetPage();

        reloadMessages();
    }, [filter]);

    const summaryColumns: ColumnsType<QueueMessageSummaryRow> = [
        {dataIndex: "type", title: "Type", sorter: (a, b) => a.type.localeCompare(b.type)},
        {dataIndex: "totalCount", title: "Total", align: "right", sorter: (a, b) => a.totalCount - b.totalCount},
        {dataIndex: "readyCount", title: "Ready", align: "right", sorter: (a, b) => a.readyCount - b.readyCount},
        {dataIndex: "scheduledCount", title: "Scheduled", align: "right", sorter: (a, b) => a.scheduledCount - b.scheduledCount},
        {dataIndex: "errorCount", title: "With error", align: "right", sorter: (a, b) => a.errorCount - b.errorCount},
        {dataIndex: "maxTries", title: "Max tries", align: "right", sorter: (a, b) => a.maxTries - b.maxTries},
        {dataIndex: "oldestQueuedAt", title: "Oldest queued", render: value => formatDateTime(value)},
        {dataIndex: "nextTry", title: "Next try", render: value => formatDateTime(value)},
    ];

    const messageColumns: ColumnsType<QueueMessage> = [
        {dataIndex: "type", title: "Type", width: 260, sorter: true},
        {
            dataIndex: "ready",
            title: "Status",
            width: 110,
            render: (_, message) => renderStatus(message)
        },
        {
            dataIndex: "lastError",
            title: "Error",
            width: 90,
            render: value => value ? <Tag color="red">error</Tag> : null
        },
        {
            dataIndex: "queuedAt",
            title: "Queued at",
            width: 160,
            render: value => formatDateTime(value),
            sorter: true,
            sortDirections: descendingFirstSortDirections
        },
        {
            dataIndex: "nextTry",
            title: "Next try",
            width: 160,
            render: value => formatDateTime(value),
            sorter: true,
            sortDirections: descendingFirstSortDirections
        },
        {
            dataIndex: "lastTry",
            title: "Last try",
            width: 160,
            render: value => formatDateTime(value),
            sorter: true,
            sortDirections: descendingFirstSortDirections
        },
        {dataIndex: "tries", title: "Tries", width: 90, align: "right", sorter: true},
        {dataIndex: "concurrencyGroup", title: "Concurrency group", width: 220, sorter: true},
        {
            dataIndex: "lastError",
            title: "Last error",
            ellipsis: true,
            sorter: true,
            render: value => value || null
        },
    ];

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}>
                    <Breadcrumb.Item>{appContext.config?.appName}</Breadcrumb.Item>
                    <Breadcrumb.Item>{title}</Breadcrumb.Item>
                </Breadcrumb>

                <h1>{title}</h1>

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} onClick={reload}/>
                </div>

                <section style={{marginBottom: 24}}>
                    <h2>Overview</h2>
                    <Table
                        rowKey="type"
                        dataSource={summary}
                        columns={summaryColumns}
                        loading={summaryLoading}
                        pagination={false}
                        size="small"
                        showSorterTooltip={false}
                    />
                </section>

                <section>
                    <h2>Messages</h2>
                    <div style={{display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12}}>
                        <Select
                            allowClear
                            placeholder="All types"
                            options={typeOptions}
                            value={filter.type}
                            onChange={type => setFilter(current => ({...current, type}))}
                            style={{minWidth: 260}}
                            showSearch
                            optionFilterProp="label"
                        />
                        <Select
                            allowClear
                            placeholder="All statuses"
                            options={statusOptions}
                            value={filter.status}
                            onChange={status => setFilter(current => ({...current, status}))}
                            style={{minWidth: 160}}
                        />
                        <Select
                            allowClear
                            placeholder="All errors"
                            options={errorOptions}
                            value={filter.hasError}
                            onChange={hasError => setFilter(current => ({...current, hasError}))}
                            style={{minWidth: 180}}
                        />
                    </div>
                    <Table
                        rowKey="id"
                        dataSource={messages?.data}
                        columns={messageColumns}
                        loading={tableHandler.loading}
                        pagination={tableHandler.pagination}
                        onChange={tableHandler.onTableChange}
                        showSorterTooltip={false}
                        size="small"
                        expandable={{expandedRowRender: renderMessageDetail}}
                    />
                </section>
            </>
        </DocumentTitle>
    );

    function reload() {
        return Promise.all([reloadSummary(), reloadMessages()])
            .then(() => undefined);
    }

    function reloadSummary() {
        setSummaryLoading(true);

        return queueService.getSummary()
            .then(setSummary)
            .finally(() => setSummaryLoading(false));
    }

    function reloadMessages() {
        tableHandler.setLoading(true);

        return queueService.getMessages(tableHandler.queryOptions, filter)
            .then(value => {
                tableHandler.updateTotal(value.total);

                setMessages(value);
            })
            .finally(() => tableHandler.setLoading(false));
    }

    function renderStatus(message: QueueMessage) {
        if (message.ready) {
            return <Tag color="green">ready</Tag>;
        }

        return <Tag color="blue">scheduled</Tag>;
    }

    function renderMessageDetail(message: QueueMessage) {
        return (
            <div style={{display: "grid", gap: 12}}>
                <div>
                    <strong>Content</strong>
                    <pre style={{whiteSpace: "pre-wrap", margin: "8px 0 0"}}>{formatJson(message.content)}</pre>
                </div>
                {message.lastError && (
                    <div>
                        <strong>Last error</strong>
                        <pre style={{whiteSpace: "pre-wrap", margin: "8px 0 0"}}>{message.lastError}</pre>
                    </div>
                )}
            </div>
        );
    }

    function formatJson(value: string) {
        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch (e) {
            return value;
        }
    }
}

export default QueuesPage;
